import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getApplicationSpaBackendUrl } from "@utils";
import { httpHeaders } from "@shared/constants/httpHeaders";
import { queryParams } from "@shared/constants/queryParams";
import { type IDocument } from "@types";

export interface IPreviewDocument extends Pick<IDocument, "id" | "type" | "identifier" | "label"> {}

export interface IPreviewBooking {
  from_date: string;
  from_time: string;
  to_date: string;
  to_time: string;
}

interface IResendMyDocumentsPreviewLinkResponse {
  isSent: boolean;
}

export const myDocumentsPreviewMagicLinkApiKey = "myDocumentsPreviewMagicLinkApi";
export const myDocumentsPreviewMagicLinkApi = createApi({
  reducerPath: myDocumentsPreviewMagicLinkApiKey,
  baseQuery: fetchBaseQuery({ baseUrl: getApplicationSpaBackendUrl() }),
  endpoints: (builder) => ({
    getMyDocumentsPreview: builder.query<IPreviewDocument[], { token: string; caseId: string | null }>({
      query: ({ token, caseId }) => ({
        url: "/my-documents-preview",
        headers: { [httpHeaders.magicToken]: token },
        params: caseId == null ? undefined : { [queryParams.caseId]: caseId },
      }),
    }),
    getMyDocumentsPreviewBooking: builder.query<IPreviewBooking, { token: string; caseId: string }>({
      query: ({ token, caseId }) => ({
        url: "/my-documents-preview/booking",
        headers: { [httpHeaders.magicToken]: token },
        params: { [queryParams.caseId]: caseId },
      }),
    }),
    downloadPreviewDocument: builder.mutation<string, { token: string; documentId: IPreviewDocument["id"] }>({
      query: ({ token, documentId }) => ({
        url: `/my-documents-preview/${documentId}/download`,
        headers: { [httpHeaders.magicToken]: token },
        responseHandler: async (response) =>
          response.ok
            ? URL.createObjectURL(
                new Blob([await response.blob()], { type: response.headers.get("Content-Type") ?? undefined }),
              )
            : response.text(),
      }),
    }),
    resendMyDocumentsPreviewLink: builder.mutation<
      IResendMyDocumentsPreviewLinkResponse,
      { token?: string; pageUrl: string; email: string }
    >({
      query: ({ token, pageUrl, email }) => ({
        url: "/my-documents-preview/resend",
        method: "POST",
        headers: token == null ? undefined : { [httpHeaders.magicToken]: token },
        body: { pageUrl, email },
      }),
    }),
  }),
});

export const {
  useGetMyDocumentsPreviewQuery,
  useGetMyDocumentsPreviewBookingQuery,
  useDownloadPreviewDocumentMutation,
  useResendMyDocumentsPreviewLinkMutation,
} = myDocumentsPreviewMagicLinkApi;
